import { Interface, ZeroAddress } from 'ethers'

import SwapAndBridgeProviderApiError from '../../classes/SwapAndBridgeProviderApiError'
import { byZkxEnv } from '../../utils/zkxEnv'
import { ZKX_TRADE_NATIVE_ALIAS_ADDRESS, ZKX_TRADE_NATIVE_TOKEN_ADDRESS } from './constants'
import { ZkxTradeStep } from './types'

/**
 * Client-side check of what the ZKX Trade backend asks the wallet to sign.
 *
 * The backend hands back raw `{to, data, valueRaw}` for every step. Trusting it
 * blindly means a compromised backend (or anyone who can tamper with its
 * responses) could point the approval or the swap at a contract of its own. So
 * the swap target must be a router pinned HERE, per chain and per aggregator,
 * and the approval must match that router (or the Permit2 pinned for it), the
 * from-token and the amount.
 *
 * What this does NOT cover: these are general-purpose aggregator routers, so
 * calldata with a hostile `receiver` / zero `minOut` still passes. That needs a
 * simulation-based check on the AccountOp itself.
 *
 * ── Adding an aggregator or a chain ─────────────────────────────────────────
 * Never copy an address from a backend response. Verify it on the chain's
 * explorer (verified source, official deployer / docs) and only then add it.
 * Code existing at the address is NOT enough: PulseChain forked Ethereum's
 * state, so LI.FI and KyberSwap bytecode sits at their Ethereum addresses on
 * 369 as frozen, unmaintained copies.
 */

/** Addresses verified 2026-09-13 (Blockscout / LI.FI deployments repo / Routescan). */
const LIFI_DIAMOND = '0x1231DEB6f5749EF6cE6943a275A1D3E7486F4EaE'
/** LI.FI deployed Monad separately — `lifinance/contracts` deployments/monad.json. */
const LIFI_DIAMOND_MONAD = '0x026F252016A7C47CDEf1F05a3Fc9E20C92a49C37'
const KYBERSWAP_META_AGGREGATION_ROUTER_V2 = '0x6131B5fae19EA4f9D964eAc0408E4408b66337b5'
const ZEROEX_ALLOWANCE_HOLDER = '0x0000000000001fF3684f28c67538d4D072C22734'
/** 0x's AllowanceHolder for chains without Cancun opcodes (Mantle). */
const ZEROEX_ALLOWANCE_HOLDER_PRE_CANCUN = '0x0000000000005E88410CcDFaDe4a5EfaE4b49562'
/** Native-coin swaps go to the router, ERC20 swaps approve and call the proxy. */
const RELAY_ROUTER_V3 = '0xb92fe925dc43a0ecde6c8b1a2709c170ec4fff4f'
const RELAY_APPROVAL_PROXY_V3 = '0xccc88a9d1b4ed6b0eaba998850414b24f1c315be'
/**
 * The targets ZKX Trade's `piteas` routes use on PulseChain — the wallet's own
 * forwarder, not the router Piteas' SDK calls (that one is the verified
 * `PiteasRouter` 0x6BF228eb…88A6, with a different selector).
 *
 * BOTH are allowed on purpose. The allowlist ships inside the bundle, so an
 * installed wallet can never learn an address added after it was built: the day
 * the backend starts handing out v2, every client that only knows v1 blocks the
 * swap outright. Keeping v1 here lets the backend serve v1 to old clients and
 * v2 to updated ones over the same window. Drop v1 once the old fleet has
 * drained AND the backend has stopped routing through it.
 *
 * Neither is verified on scan.pulsechain.com, and they have different deployers
 * (v1 0xD70994e8…3868, v2 0x58ac18aA…E286) — still pending the backend
 * publishing sources / ownership for both.
 */
const PITEAS_ZKX_TRADE_TARGET_V1 = '0x2d2afcb592920b9fe9173b59b2b2b711a2a2aba6'
const PITEAS_ZKX_TRADE_TARGET_V2 = '0x3558198f756bc26aeae9de8c646e9f9bb842e519'
/**
 * Switch (switch.win) on PulseChain, the aggregator ZKX Trade's `switch` routes
 * call directly (approve + swap on the same router). Verified 2026-10-19: it is
 * `SWITCH_ROUTER` in the official `@switch-win/sdk` 1.2.9 constants, and a
 * verified `SwitchRouter` source on scan.pulsechain.com (not a proxy), deployed
 * by 0xf3b5e21B…eAd4 — the same deployer as the SDK's `LEGACY_SWITCH_ROUTER`.
 */
const SWITCH_ROUTER_PULSECHAIN = '0x2dFc8B6e13fF7F04e37Ef97006084805D65a6F19'
/**
 * The ZKX forwarder ZKX Trade's `libertyx` routes use on PulseChain (approve +
 * swap on the same contract, plan probed 2026-10-22). Per environment:
 * - dev: the backend (apis.zkxteam.xyz/trade) routes through 0xf55F338f…7c4F,
 *   redeployed 2026-10-25 (plan probed 2026-10-28). 0x5836ABD3…ddd4 is the
 *   previous one, kept while builds that only know it are still around.
 * - uat/prod: 0xd46Cc1e1…1FDf, deployed 2026-10-26 by 0x58ac18aA…E286 (the
 *   `PITEAS_ZKX_TRADE_TARGET_V2` deployer).
 * `LIBERTYX_FORWARDER_ADDRESS` (see `setZkxTradeEnvTargets`) can add one more
 * without a submodule bump, but the table must not depend on it: the dev
 * deployment shipped without the key and blocked every `libertyx` swap with
 * "Swap blocked: unverified transaction" once the backend moved to 0xf55F.
 *
 * None of the three is verified on scan.pulsechain.com and all share the same
 * bytecode; the dev ones were deployed by 0xD70994e8…3868, the same deployer as
 * `PITEAS_ZKX_TRADE_TARGET_V1`.
 */
/**
 * PancakeSwap Infinity on BNB Chain, which ZKX Trade's `pancakeswap` routes call
 * (`execute(bytes,bytes[],uint256)`, plan probed on the dev backend 2026-10-29).
 * Verified 2026-10-29: `UniversalRouter` for BSC on PancakeSwap's Infinity
 * addresses page (developer.pancakeswap.finance/contracts/infinity/resources/addresses).
 *
 * The router pulls an ERC20 through Permit2 instead of `transferFrom`, so its
 * plan is approve(token → Permit2) + `Permit2.approve(token, router, amount,
 * expiration)` + swap. 0x31c2F6fc…c768 is the `permit2` the router itself was
 * deployed with (`script/deployParameters/mainnet/DeployBsc.s.sol` in
 * pancakeswap/infinity-universal-router). It is never a swap target.
 */
const PANCAKESWAP_INFINITY_UNIVERSAL_ROUTER_BSC = '0xd9C500DfF816a1Da21A48A732d3498Bf09dc9AEB'
const PANCAKESWAP_PERMIT2 = '0x31c2F6fcFf4F8759b3Bd5Bf0e1084A055615c768'
const LIBERTYX_FORWARDER_PULSECHAIN = byZkxEnv<string[]>({
  dev: ['0xf55F338f3616c9b4ADa6bf22bEC2226b422d7c4F', '0x5836ABD3f93a6e304059aBB965E6D9349C15ddd4'],
  uat: ['0xd46Cc1e175320c210FB3090C9B5269a710BD1FDf'],
  prod: ['0xd46Cc1e175320c210FB3090C9B5269a710BD1FDf']
})

type Aggregator =
  | 'lifi'
  | 'kyberswap'
  | 'zeroex'
  | 'relay'
  | 'piteas'
  | 'switch'
  | 'libertyx'
  | 'pancakeswap'

const RELAY = [RELAY_ROUTER_V3, RELAY_APPROVAL_PROXY_V3]

const ALLOWED_TARGETS_BY_CHAIN: Record<number, Partial<Record<Aggregator, string[]>>> = {
  1: {
    lifi: [LIFI_DIAMOND],
    kyberswap: [KYBERSWAP_META_AGGREGATION_ROUTER_V2],
    zeroex: [ZEROEX_ALLOWANCE_HOLDER],
    relay: RELAY
  },
  10: {
    lifi: [LIFI_DIAMOND],
    kyberswap: [KYBERSWAP_META_AGGREGATION_ROUTER_V2],
    zeroex: [ZEROEX_ALLOWANCE_HOLDER],
    relay: RELAY
  },
  56: {
    lifi: [LIFI_DIAMOND],
    kyberswap: [KYBERSWAP_META_AGGREGATION_ROUTER_V2],
    zeroex: [ZEROEX_ALLOWANCE_HOLDER],
    relay: RELAY,
    pancakeswap: [PANCAKESWAP_INFINITY_UNIVERSAL_ROUTER_BSC]
  },
  137: {
    lifi: [LIFI_DIAMOND],
    kyberswap: [KYBERSWAP_META_AGGREGATION_ROUTER_V2],
    zeroex: [ZEROEX_ALLOWANCE_HOLDER],
    relay: RELAY
  },
  143: {
    lifi: [LIFI_DIAMOND_MONAD],
    zeroex: [ZEROEX_ALLOWANCE_HOLDER],
    relay: RELAY
  },
  369: {
    piteas: [PITEAS_ZKX_TRADE_TARGET_V1, PITEAS_ZKX_TRADE_TARGET_V2],
    switch: [SWITCH_ROUTER_PULSECHAIN],
    libertyx: LIBERTYX_FORWARDER_PULSECHAIN
  },
  5000: {
    lifi: [LIFI_DIAMOND],
    zeroex: [ZEROEX_ALLOWANCE_HOLDER_PRE_CANCUN]
  },
  8453: {
    lifi: [LIFI_DIAMOND],
    kyberswap: [KYBERSWAP_META_AGGREGATION_ROUTER_V2],
    zeroex: [ZEROEX_ALLOWANCE_HOLDER],
    relay: RELAY
  },
  42161: {
    lifi: [LIFI_DIAMOND],
    kyberswap: [KYBERSWAP_META_AGGREGATION_ROUTER_V2],
    zeroex: [ZEROEX_ALLOWANCE_HOLDER],
    relay: RELAY
  },
  43114: {
    lifi: [LIFI_DIAMOND],
    kyberswap: [KYBERSWAP_META_AGGREGATION_ROUTER_V2],
    zeroex: [ZEROEX_ALLOWANCE_HOLDER],
    relay: RELAY
  },
  534352: {
    lifi: [LIFI_DIAMOND],
    zeroex: [ZEROEX_ALLOWANCE_HOLDER],
    relay: RELAY
  }
}

/**
 * The Permit2 an aggregator's router pulls tokens through, for the aggregators
 * that do. Only these get a `Permit2.approve` step through the guard, and only
 * at this exact address.
 */
const PERMIT2_BY_CHAIN: Record<number, Partial<Record<Aggregator, string>>> = {
  56: { pancakeswap: PANCAKESWAP_PERMIT2 }
}

/**
 * Longest `Permit2.approve` expiration the wallet signs. The backend sends
 * ~18 minutes; a day is headroom for a slow EOA batch, not a standing allowance.
 */
const PERMIT2_MAX_EXPIRATION_SECONDS = 24 * 60 * 60

const APPROVE_SELECTOR = '0x095ea7b3'

const ERC20_APPROVE = new Interface([
  'function approve(address spender, uint256 amount) returns (bool)'
])

const PERMIT2_APPROVE = new Interface([
  'function approve(address token, address spender, uint160 amount, uint48 expiration)'
])

const sameAddress = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

// All three spellings. A native swap's plan carries `valueRaw === amountIn`, so
// reading 0xEeee…EEeE as an ERC20 here blocks it with "transaction value does
// not match the swap" — the guard failing right behind the `/routes` fix.
const isNativeTokenAddress = (address: string) =>
  sameAddress(address, ZKX_TRADE_NATIVE_TOKEN_ADDRESS) ||
  sameAddress(address, ZeroAddress) ||
  sameAddress(address, ZKX_TRADE_NATIVE_ALIAS_ADDRESS)

/**
 * Routers pushed in from the build's `.env` at boot (`LIBERTYX_FORWARDER_ADDRESS`,
 * `SWITCH_ROUTER_ADDRESSES`, read in `common/config/env/env.ts`). They are ADDED
 * to the table above, never replace it, so a stale or empty key cannot drop a
 * router the wallet already trusts. Same trust level as the table: the value is
 * baked into the bundle by CI, it never comes from the backend.
 */
const envTargetsByChain: Record<number, Partial<Record<Aggregator, string[]>>> = {}

/** Call once at boot. Invalid entries are ignored (validateEnv rejects them first). */
export const setZkxTradeEnvTargets = (
  chainId: number,
  aggregator: Aggregator,
  addresses: string[]
): void => {
  const valid = addresses.filter((address) => /^0x[0-9a-fA-F]{40}$/.test(address))
  envTargetsByChain[chainId] = { ...envTargetsByChain[chainId], [aggregator]: valid }
}

const getAllowedTargets = (chainId: number, provider: string): string[] => {
  const aggregator = provider?.toLowerCase() as Aggregator
  return [
    ...(ALLOWED_TARGETS_BY_CHAIN[chainId]?.[aggregator] || []),
    ...(envTargetsByChain[chainId]?.[aggregator] || [])
  ]
}

/**
 * Whether the wallet would sign for this aggregator on this chain at all. Used
 * at quote time so an unvetted route is never offered, rather than offered and
 * then refused on Proceed.
 */
export const isZkxTradeProviderAllowed = (chainId: number, provider: string): boolean =>
  getAllowedTargets(chainId, provider).length > 0

export const isZkxTradeTargetAllowed = (
  chainId: number,
  provider: string,
  target: string
): boolean => getAllowedTargets(chainId, provider).some((allowed) => sameAddress(allowed, target))

const block = (reason: string): never => {
  throw new SwapAndBridgeProviderApiError(
    `The swap was blocked because ZKX Trade returned a transaction the wallet could not verify: <${reason}>`,
    'Swap blocked: unverified transaction.'
  )
}

const getPermit2 = (chainId: number, provider: string): string | undefined =>
  PERMIT2_BY_CHAIN[chainId]?.[provider?.toLowerCase() as Aggregator]

export const isZkxTradeApproveStep = (step: ZkxTradeStep): boolean =>
  !!step.payload?.data?.toLowerCase().startsWith(APPROVE_SELECTOR)

export type VerifiedZkxTradePlan = {
  chainId: number
  swapTarget: string
  swapData: string
  value: string
  approval: { token: string; spender: string; amount: string } | null
  /**
   * The router's allowance inside Permit2, for aggregators that pull through it
   * (PancakeSwap). Set only when the plan carries the `Permit2.approve` step;
   * `approval` then points at Permit2 rather than the router.
   */
  permit2Approval?: {
    permit2: string
    token: string
    spender: string
    amount: string
    expiration: string
  }
  /**
   * Id of the swap step — the plan's LAST `TRANSACTION` step, since the approve
   * (when there is one) always comes first. `POST /trades/:id/broadcast` is
   * reported against it. Optional because the backend types `id` as optional;
   * in practice every step carries one (`transaction-0`, `transaction-1`, ...).
   */
  swapStepId?: string
}

/**
 * Checks every executable step of a plan against the route the user picked and
 * returns the parts the wallet may sign. Throws on anything unexpected — there
 * is no "warn and continue" here.
 */
export const verifyZkxTradePlan = ({
  steps,
  chainId,
  provider,
  fromTokenAddress,
  fromAmount
}: {
  steps: ZkxTradeStep[]
  /** The route's from-chain. */
  chainId: number
  /** The aggregator the user picked (`route.rawRoute.provider`). */
  provider: string
  fromTokenAddress: string
  /** Raw amount the user is swapping; the integrator fee is taken out of it. */
  fromAmount: string
}): VerifiedZkxTradePlan => {
  // `AWAIT_SETTLEMENT` is a polling instruction without `to` / `data`.
  const txSteps = steps.filter((step) => step.payload?.to || step.payload?.data)

  txSteps.forEach((step) => {
    const { to, data } = step.payload || {}
    if (!to || !/^0x[0-9a-fA-F]{40}$/.test(to)) block(`step target <${to}> is not an address`)
    if (!data || !/^0x[0-9a-fA-F]*$/.test(data)) block('step calldata is not hex')
    if (step.payload?.chainId !== undefined && Number(step.payload.chainId) !== chainId)
      block(`step is for chain ${step.payload.chainId}, the swap is on chain ${chainId}`)
  })

  const permit2 = getPermit2(chainId, provider)
  const isPermit2Step = (step: ZkxTradeStep) =>
    !!permit2 && sameAddress(step.payload?.to, permit2) && !isZkxTradeApproveStep(step)

  const approveSteps = txSteps.filter(isZkxTradeApproveStep)
  const permit2Steps = txSteps.filter(isPermit2Step)
  const swapSteps = txSteps.filter((step) => !isZkxTradeApproveStep(step) && !isPermit2Step(step))

  if (swapSteps.length !== 1) block(`expected one swap transaction, got ${swapSteps.length}`)
  if (approveSteps.length > 1) block(`expected at most one approval, got ${approveSteps.length}`)
  if (permit2Steps.length > 1)
    block(`expected at most one Permit2 approval, got ${permit2Steps.length}`)

  const swapPayload = swapSteps[0]!.payload!
  const swapTarget = swapPayload.to!

  if (!isZkxTradeTargetAllowed(chainId, provider, swapTarget))
    block(`${swapTarget} is not a known ${provider} router on chain ${chainId}`)

  let amountIn: bigint
  try {
    amountIn = BigInt(fromAmount)
  } catch {
    return block('invalid swap amount')
  }

  const isNativeFrom = isNativeTokenAddress(fromTokenAddress)

  let value: bigint
  try {
    value = BigInt(swapPayload.valueRaw || '0')
  } catch {
    return block('invalid transaction value')
  }
  // Native coin leaves as `value` and must be exactly the amount being swapped;
  // an ERC20 swap moves no native coin at all.
  if (isNativeFrom ? value !== amountIn : value !== 0n)
    block(`transaction value ${value} does not match the swap`)

  let permit2Approval: VerifiedZkxTradePlan['permit2Approval']
  if (permit2Steps.length) {
    if (isNativeFrom) block('a native-coin swap must not carry a Permit2 approval')

    let token: string
    let spender: string
    let amount: bigint
    let expiration: bigint
    try {
      const decoded = PERMIT2_APPROVE.decodeFunctionData('approve', permit2Steps[0]!.payload!.data!)
      token = decoded[0] as string
      spender = decoded[1] as string
      amount = decoded[2] as bigint
      expiration = decoded[3] as bigint
    } catch {
      return block('Permit2 approval calldata could not be decoded')
    }

    if (BigInt(permit2Steps[0]!.payload!.valueRaw || '0') !== 0n)
      block('the Permit2 approval must not move native coin')
    if (!sameAddress(token, fromTokenAddress))
      block(`Permit2 approval is for token ${token}, the swap spends ${fromTokenAddress}`)
    if (!sameAddress(spender, swapTarget))
      block(`Permit2 approval spender ${spender} is not the swap router ${swapTarget}`)
    if (amount > amountIn)
      block(`Permit2 approval of ${amount} exceeds the swap amount ${amountIn}`)

    // Permit2 reads 0 as "this block only", and a far-off expiration would turn
    // a one-off swap into a standing allowance for the router.
    const now = BigInt(Math.floor(Date.now() / 1000))
    if (expiration <= now) block('Permit2 approval has already expired')
    if (expiration > now + BigInt(PERMIT2_MAX_EXPIRATION_SECONDS))
      block(`Permit2 approval expires too far ahead (${expiration})`)

    permit2Approval = {
      permit2: permit2!,
      token: fromTokenAddress,
      spender: swapTarget,
      amount: amount.toString(),
      expiration: expiration.toString()
    }
  }

  let approval: VerifiedZkxTradePlan['approval'] = null
  if (approveSteps.length) {
    if (isNativeFrom) block('a native-coin swap must not carry an approval')

    const approvePayload = approveSteps[0]!.payload!
    if (!sameAddress(approvePayload.to, fromTokenAddress))
      block(`approval is for token ${approvePayload.to}, the swap spends ${fromTokenAddress}`)

    let spender: string
    let amount: bigint
    try {
      const decoded = ERC20_APPROVE.decodeFunctionData('approve', approvePayload.data!)
      spender = decoded[0] as string
      amount = decoded[1] as bigint
    } catch {
      return block('approval calldata could not be decoded')
    }

    // The approval must go to the very router that is called — or, for a router
    // that pulls through Permit2, to that Permit2 — and never for more than is
    // being swapped (no unlimited approvals from the backend).
    const expectedSpender = permit2Approval ? permit2Approval.permit2 : swapTarget
    if (!sameAddress(spender, expectedSpender))
      block(`approval spender ${spender} is not the swap router ${expectedSpender}`)
    if (amount > amountIn) block(`approval of ${amount} exceeds the swap amount ${amountIn}`)

    approval = { token: approvePayload.to!, spender, amount: amount.toString() }
  } else if (permit2Approval) {
    // The backend leaves the ERC20 → Permit2 approve out when it sees one in
    // place. Still hand the wallet that approval: `getSwapAndBridgeCalls` checks
    // the live allowance and skips it when it covers the swap, and without it a
    // spent or revoked Permit2 allowance would only surface as a revert.
    approval = {
      token: fromTokenAddress,
      spender: permit2Approval.permit2,
      amount: permit2Approval.amount
    }
  }

  return {
    chainId,
    swapTarget,
    swapData: swapPayload.data!,
    value: value.toString(),
    approval,
    ...(permit2Approval ? { permit2Approval } : {}),
    swapStepId: swapSteps[0]!.id
  }
}
